/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import {
  modalController,
  popoverController,
} from '@ionic/vue';
import ChartConst from '../constants/chart.constant';
import AppConst from '../constants/app.constant';
import bnd3Service from './bnd3.service';
import {
  is_iphone,
} from './utils/is_iphone';
import { is_ipad } from './utils/is_ipad';
import utilityService from './utility.service';
import routeProfileDataSource from '../views/Routeprofile/Routeprofile.data';
import ConfigApplicationSettings from '../config/application.config';
import ConfigDeviceSettings from '../config/device.config';
import routeProfileService from '../views/Routeprofile/Routeprofile.service';
import etdService from './etd.service';
import RouteForm from '../components/RouteForm.vue';
import QuickChanges from '../components/QuickChanges/QuickChanges.vue';
import d3 from '../globalexports/d3.export';
import router from '../router';
import { routerPushInterface } from './router.interface';
import * as AltitudeOptionsConst from '../constants/altitude.constant';

let timer;

const routeControllerService = () => {
  const content = {};

  function getDataSourceByState() {
    return routeProfileDataSource;
  }
  content.setDateFormat = (settings) => {
    let dateFormat;
    if (settings.timeZone === AppConst.timeZones.local.key) {
      dateFormat = d3.timeFormat(
        ChartConst.travelTimeRangeDateFormatLocal + ChartConst.GraphConfig.xAxis.timePostFix
          .local,
      );
    } else {
      dateFormat = d3.utcFormat(ChartConst.travelTimeRangeDateFormat + AppConst.timeZones.zulu
        .key);
    }
    return dateFormat;
  };
  content.loadViewVairables = (ctrlObj) => {
    const queryParam = {
      start: router.currentRoute.value.query.start,
      end: router.currentRoute.value.query.end,
      mid1: router.currentRoute.value.query.mid1,
      mid2: router.currentRoute.value.query.mid2,
      ruleset: router.currentRoute.value.query.ruleset,
    };
    if (!router.currentRoute.value.query.start) {
      queryParam.start = (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.start
        : ctrlObj.settings.start);
    }
    if (!router.currentRoute.value.query.end) {
      queryParam.end = (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.end
        : ctrlObj.settings.end);
    }

    if (!router.currentRoute.value.query.mid1 && !router.currentRoute.value.query.isFromFav) {
      queryParam.mid1 = (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.mid1
        : ctrlObj.settings.mid1);
    }
    if (!router.currentRoute.value.query.mid2 && !router.currentRoute.value.query.isFromFav) {
      queryParam.mid2 = (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.mid2
        : ctrlObj.settings.mid2);
    }
    if (!queryParam.mid1 || queryParam.mid1 === '') {
      delete queryParam.mid1;
    }
    if (!queryParam.mid2 || queryParam.mid2 === '') {
      delete queryParam.mid2;
    }
    if (!queryParam.ruleset) {
      delete queryParam.ruleset;
    }
    routerPushInterface({
      query: queryParam,
    });
    if (!queryParam.mid1) {
      ctrlObj.settings.mid1 = '';
    } else {
      ctrlObj.settings.mid1 = queryParam.mid1;
    }
    if (!queryParam.mid2) {
      ctrlObj.settings.mid2 = '';
    } else {
      ctrlObj.settings.mid2 = queryParam.mid2;
    }
    ctrlObj.settings.start = queryParam.start;
    ctrlObj.settings.end = queryParam.end;
    // By default product in route profile starts with LAMP(24hrs)
    ctrlObj.settings.product = ChartConst.products.lamp.name;
    getDataSourceByState().routeFormSettings = utilityService.clone(ctrlObj.settings);
  };
  function getWorstRuleFromListOfAirport(listOfAirport) {
    let i = 0;
    let ruleIndex;
    let worstRuleIndex = 4;
    while (i < listOfAirport.length) {
      ruleIndex = listOfAirport[i].frlSlider;
      if (ruleIndex < worstRuleIndex && ruleIndex !== null) {
        worstRuleIndex = ruleIndex;
      }
      i += 1;
    }
    return worstRuleIndex;
  }
  content.getWorstRuleSet = (obj) => {
    let worstRuleIndex;
    let worstRuleObj = {
      ruleName: '--',
      ruleColor: ChartConst.flightRuleColorIfNull,
    };
    if (obj) {
      if (ConfigApplicationSettings.settings.ignoreMidpoints) {
        // IF ignoreMidpoints is on then only use START-END
        // for deciding worst rule for slider dot color
        worstRuleIndex = getWorstRuleFromListOfAirport([
          obj.travelRoute[0],
          obj.travelRoute[obj.travelRoute.length - 1],
        ]);
      } else {
        worstRuleIndex = getWorstRuleFromListOfAirport(obj.travelRoute);
      }
      worstRuleObj = {
        ruleName: bnd3Service.getFlightDotText(worstRuleIndex),
        ruleColor: bnd3Service.getFlightDotColor(worstRuleIndex),
      };
    }
    return worstRuleObj;
  };
  content.slideSlider = (ctrlObj, sliderMainObj, dir, sidenavCtrl) => {
    if (dir !== 'play') {
      ctrlObj.stopImageCycle();
    }
    if (ctrlObj.currentSliderIndex === sliderMainObj.length) {
      ctrlObj.currentSliderIndex = sliderMainObj.length - 1;
    }
    if (dir === 'play' || (ctrlObj.currentSliderIndex < sliderMainObj.length && ctrlObj
      .currentSliderIndex >= 0)) {
      if (ctrlObj.currentSliderIndex === sliderMainObj.length - 1) {
        // if play then do it in loop
        if (dir === 'play') {
          dir = 'down';
        }
        ctrlObj.currentSliderIndex = -1;
      }
      if (dir === 'up') {
        ctrlObj.currentSliderIndex = Math.max(ctrlObj.currentSliderIndex - 1, 0);
        ctrlObj.settings.departureTime = sliderMainObj[ctrlObj.currentSliderIndex]
          || sliderMainObj[0];
      } else {
        ctrlObj.currentSliderIndex += 1;
        ctrlObj.currentSliderIndex = Math.min(ctrlObj.currentSliderIndex, sliderMainObj.length
          - 1);
        ctrlObj.settings.departureTime = sliderMainObj[ctrlObj.currentSliderIndex]
          || sliderMainObj[sliderMainObj.length - 1];
      }
    }
    sidenavCtrl.setDepartureTime(ctrlObj.settings.departureTime);
    sidenavCtrl.saveDepartureTimeToLs();
  };
  content.getAllIntervalData = (
    sliderMainObj,
    ctrlObj,
    allDataForInterval,
    sliderMainObjSecondary,
  ) => {
    let i = 0;
    const tempSettingsObj = utilityService.clone(ctrlObj.settings);
    let tmpObj;
    const loopOversliderMainObj = sliderMainObjSecondary || sliderMainObj;
    while (i < loopOversliderMainObj.length) {
      tempSettingsObj.departureTime = loopOversliderMainObj[i];
      tmpObj = routeProfileService.getWorstRuleForDeparture(
        ctrlObj.routeProfileData,
        tempSettingsObj,
      );
      if (allDataForInterval.length < sliderMainObj.length) {
        allDataForInterval.push(tmpObj);
      }
      i += 1;
    }
  };
  content.startImageCycle = (ctrlObj, deviceSpecificSetting) => {
    ctrlObj.stopImageCycle();
    if (!routeProfileService.cycleImageAccordionInterval) {
      ctrlObj.timerControlClass = 'ion-ios-pause';
      ctrlObj.runningCycle = true;
      let delayTimer = deviceSpecificSetting.sliderDelayEtd;
      if (delayTimer) {
        delayTimer = parseInt(delayTimer, 10);
      }
      if (Number.isNaN(Number(delayTimer))) {
        delayTimer = 1000;
      }
      routeProfileService.cycleImageAccordionInterval = setInterval(() => {
        ctrlObj.slideSlider('play');
        if (ctrlObj.updateTravelRouteLiveHandle) {
          ctrlObj.updateTravelRouteLiveHandle();
        } else if (ctrlObj.drawOnSlideWith) {
          ctrlObj.drawOnSlideWith();
        } else if (ctrlObj.drawOnSlideWithoutDeBounce) {
          ctrlObj.drawOnSlideWithoutDeBounce();
          ctrlObj.drawOnSlideWithDeBounce();
        }
      }, delayTimer);
    }
  };
  content.stopImageCycle = (ctrlObj, dontDrawFresh) => {
    if (routeProfileService.cycleImageAccordionInterval) {
      window.clearInterval(routeProfileService.cycleImageAccordionInterval);
      delete routeProfileService.cycleImageAccordionInterval;
      ctrlObj.runningCycle = false;
      ctrlObj.timerControlClass = 'ion-ios-play';
      if (ctrlObj.updateTravelRouteLiveHandle && ctrlObj.drawOnSlideWith && !dontDrawFresh) {
        ctrlObj.drawOnSlideWith();
      }
    }
    if (routeProfileService.cycleImageAccordionDelayRestart) {
      window.clearInterval(routeProfileService.cycleImageAccordionDelayRestart);
      delete routeProfileService.cycleImageAccordionDelayRestart;
    }
  };
  content.actionForTimer = (ctrlObj) => {
    if (ctrlObj.runningCycle) {
      ctrlObj.stopImageCycle();
    } else {
      ctrlObj.startImageCycle();
    }
  };
  content.nextAppWalkThrough = (action, sidenavCtrl, ctrlObj, scope) => {
    // We need seperate nextAppWalkThrough for each view to support individual behaviour
    // For ex: if we want to open routeprofile slider on route profile screen
    // Then we will call that function after nextsstage is initialised
    // ionic.requestAnimationFrame(() => {
    if (action === AppConst.walthroughAction.previous) {
      sidenavCtrl.appWalkThrough.currentIndex -= 1;
    } else if (action === AppConst.walthroughAction.next) {
      sidenavCtrl.appWalkThrough.currentIndex += 1;
    }
    const nextStage = sidenavCtrl.appWalkThrough.steps[sidenavCtrl.appWalkThrough.currentIndex];
    if (nextStage) {
      sidenavCtrl.handleSideNavHelp(nextStage);
      if (nextStage.callBack) {
        if (ctrlObj[nextStage.callBack]) {
          ctrlObj[nextStage.callBack]();
        } else if (scope[nextStage.callBack]) {
          scope[nextStage.callBack]();
        }
      }
      sidenavCtrl.showNextStage();
    }
    // });
  };
  content.closeRouteProfileFormModal = (
    isSave,
    scope,
    ctrlObj,
    validateRoute,
    syncRouteData,
    initMapView,
    sidenavCtrl,
  ) => {
    if (isSave) {
      if (ctrlObj.stopImageCycle) {
        ctrlObj.stopImageCycle();
      }
      // $ionicHistory.clearCache();
      sidenavCtrl.departureTime = null;
      if (utilityService.hasValidAirportSelected(scope.airportDepartureString, scope
        .formViewSettings, 'selectedDepartureAirport') && utilityService
        .hasValidAirportSelected(
          scope.airportDestinationString,
          scope.formViewSettings,
          'selectedDestinationAirport',
        )) {
        if (validateRoute().isValidRoute) {
          ctrlObj.settings.start = scope.formViewSettings.selectedDepartureAirport
            .originalObject.icao_id;
          ctrlObj.settings.end = scope.formViewSettings.selectedDestinationAirport
            .originalObject.icao_id;
          if (scope.formViewSettings.selectedMid1Airport && scope.formViewSettings
            .selectedMid1Airport.originalObject) {
            ctrlObj.settings.mid1 = scope.formViewSettings.selectedMid1Airport.originalObject
              .icao_id;
          } else {
            ctrlObj.settings.mid1 = '';
          }
          if (scope.formViewSettings.selectedMid2Airport
            && scope.formViewSettings.selectedMid2Airport.originalObject) {
            ctrlObj.settings.mid2 = scope.formViewSettings.selectedMid2Airport.originalObject
              .icao_id;
          } else {
            ctrlObj.settings.mid2 = '';
          }
          ctrlObj.settings.showHeadWindOnly = scope.formViewSettings.showHeadWindOnly;
          ctrlObj.settings.maximumAltitudeAllowed = scope.formViewSettings
            .maximumAltitudeAllowed;
          if (ctrlObj.settings.start && ctrlObj.settings.end) {
            getDataSourceByState().routeFormSettings = utilityService.clone(ctrlObj.settings);
            syncRouteData();
            initMapView(true);
          }
          ctrlObj.routeProfileFormModal.hide();
        }
      }
    } else if (ctrlObj.routeProfileFormModal) {
      ctrlObj.routeProfileFormModal.hide();
    }
  };
  content.syncRouteData = (settings) => {
    // has atlest settings enabled
    if (settings && settings.start && settings.end) {
      const queryParam = {
        start: settings.start.toUpperCase(),
        end: settings.end.toUpperCase(),
        ruleset: router.currentRoute.value.query.ruleset,
      };
      if (settings.mid1) {
        queryParam.mid1 = settings.mid1.toUpperCase();
      } else {
        queryParam.mid1 = null;
      }
      if (settings.mid2) {
        queryParam.mid2 = settings.mid2.toUpperCase();
      } else {
        queryParam.mid2 = null;
      }
      if (!queryParam.ruleset) {
        delete queryParam.ruleset;
      }
      routerPushInterface({
        query: queryParam,
      });
      utilityService.setSettingInLocalstorage(
        settings,
        AppConst.localstorageConf.routeprofileSettingKey,
      );
    }
  };
  content.getSettingRouteObj = (applicationSettings, deviceSpecificSetting, ctrlObj) => {
    let sliderButtonOffset;
    const settingObj = {
      start: (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.start
        : router.currentRoute.value.query.start || ChartConst.defaultStart), // Supported
      end: (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.end
        : router.currentRoute.value.query.end || ChartConst.defaultEnd),
      mid1: (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.mid1
        : router.currentRoute.value.query.mid1 || ''),
      mid2: (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.mid2
        : router.currentRoute.value.query.mid2 || ''),
      maximumAltitudeAllowed: (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.maximumAltitudeAllowed === undefined
          ? AltitudeOptionsConst.MSL12K.feet
          : getDataSourceByState().routeFormSettings.maximumAltitudeAllowed
        : AltitudeOptionsConst.MSL12K.feet),
      showHeadWindOnly: (getDataSourceByState().routeFormSettings
        ? getDataSourceByState().routeFormSettings.showHeadWindOnly === undefined
          ? AppConst.defaultHeadOnlyWind
          : getDataSourceByState().routeFormSettings.showHeadWindOnly
        : AppConst.defaultHeadOnlyWind),
      sliderPinned: true,
      departureTime: utilityService.getDepartureTime(deviceSpecificSetting
        .leftAnchor), // Considering current time as default departure time
      ruleset: ChartConst.defaultRuleset, // Supported
      groundSpeed: applicationSettings.groundSpeed,
      product: router.currentRoute.value.query.product || ChartConst.defaultProduct,
      timeZone: applicationSettings.timeZone,
      temperatureUnit: applicationSettings.temperatureUnit,
      enableCloudBelowCig: deviceSpecificSetting.enableCloudBelowCig,
      disableRouteValidation: deviceSpecificSetting.disableRouteValidation,
      viewName: router.currentRoute.value.name,
      testmode: deviceSpecificSetting.enableTestMode,
      testRouteTrend: deviceSpecificSetting.testRouteTrend, // Supported
    };
    // incase these values are missing set it to these
    if (!settingObj.maximumAltitudeAllowed) {
      settingObj.maximumAltitudeAllowed = AltitudeOptionsConst.FL20K.feet;
    }
    if (settingObj.showHeadWindOnly !== true && settingObj.showHeadWindOnly !== false) {
      settingObj.showHeadWindOnly = true;
    }
    settingObj.dataMode = AppConst.dataMode.routeprofile;
    ctrlObj.ChartConst = ChartConst;
    ctrlObj.currentSliderIndex = null;
    // On Route profile load set departure time to current time : necessary
    // settingObj.departureTime = utilityService.getDepartureTime();
    // ToDO move to route constant and a service
    if (is_iphone()) {
      sliderButtonOffset = 50;
      ctrlObj.sliderMargin = 1.5;
    } else if (is_ipad()) {
      sliderButtonOffset = 60;
      ctrlObj.sliderMargin = -3;
    } else {
      sliderButtonOffset = 66;
      ctrlObj.sliderMargin = -7;
    }
    ctrlObj.sliderWidth = window.innerWidth - (ConfigApplicationSettings.showMobileView
      ? 0
      : ((window.innerWidth * 10) / 100)) - sliderButtonOffset;
    return settingObj;
  };
  content.tapOutsideListener = (ctrlObj) => {
    setTimeout(() => {
      // Run this in next digest cycle
      ctrlObj.showSlider = false;
    });
  };
  content.checkIfAnyAirportChanged = (settings, routeFormSettings) => {
    let changed = false;
    if (settings && routeFormSettings) {
      if (routeFormSettings.start !== settings.start) {
        changed = true;
      }
      if (settings.end !== routeFormSettings.end) {
        changed = true;
      }
      if (settings.mid1 !== routeFormSettings.mid1) {
        changed = true;
      }
      if (settings.mid2 !== routeFormSettings.mid2) {
        changed = true;
      }
    }
    return changed;
  };
  content.loadRouteprofileSetting = (vm) => {
    if (ConfigApplicationSettings.settings) {
      routeProfileDataSource.refreshRouteFormSettings();
      let oldDepartrueTime;
      if (vm.settings && vm.settings.departureTime) {
        oldDepartrueTime = vm.settings.departureTime;
      }
      vm.settings = content.getSettingRouteObj(
        ConfigApplicationSettings.settings,
        ConfigDeviceSettings.settings,
        vm,
      );
      if (oldDepartrueTime) {
        vm.settings.departureTime = oldDepartrueTime;
      }
      vm.setDateFormat();
      vm.departTime = ChartConst.departurePrefix + vm.dateFormat(vm.settings.departureTime);
      vm.settings.sliderPinnedOldState = vm.settings.sliderPinned;
      if (utilityService.hideBottomAxis()) {
        vm.settings.sliderPinned = true;
      }
      if (vm.settings.sliderPinned) {
        vm.showSlider = true;
      }
    }
  };
  content.toggleSlider = (event, vm) => {
    if (vm.sliderMainObj && vm.sliderMainObj.length) {
      vm.showSlider = !vm.showSlider;
      if (vm.settings.sliderPinned) {
        vm.settings.sliderPinned = false;
        vm.settings.sliderPinnedOldState = false;
        vm.showSlider = false;
        vm.updateTravelRoute();
        vm.syncRouteData();
      }
      event.stopPropagation();
    }
  };
  content.cleanUpSpace = (vm) => {
    vm.sliderMainObj = [];
    vm.sliderMainObjSecondary = null;
    vm.allDataForInterval = [];
    vm.currentSliderIndex = null;
  };
  content.togglePin = (state, vm) => {
    vm.showSlider = true;
    vm.settings.sliderPinned = state;
    vm.settings.sliderPinnedOldState = state;
    vm.syncRouteData();
    setTimeout(() => {
      vm.updateTravelRoute();
    }, 100);
  };
  content.loadSliderDatesDependency = (data, vm) => {
    // Slider range max-limit is 72hrs - flightDuratio
    const sliderMinutesRange = ChartConst.travelTimeRangeMinutes - (data.totalFlightDuration
      * 60);
    vm.setDateFormat();
    const startLampFrom = data.lampStartAt;
    vm.sliderMainObj = utilityService.getAllDatesInNextXMinutes(
      sliderMinutesRange,
      data.mavEndsAt,
      data.totalFlightDuration,
      ConfigDeviceSettings.settings.leftAnchor,
      startLampFrom,
    );
    if (router.currentRoute.value.name === 'app.mapview') {
      /**
       * To load travel routes for extra time of flight slider in mapview
       */
      vm.sliderMainObjSecondary = utilityService.getAllDatesInNextXMinutes(
        ChartConst.travelTimeRangeMinutes,
        data.mavEndsAt,
        data.totalFlightDuration,
        ConfigDeviceSettings.settings.leftAnchor,
        startLampFrom,
        true,
      );
    }
  };
  content.loadSliderDataAndProfileData = (data, vm) => {
    vm.cleanUpSpace();
    vm.loadSliderDatesDependency(data);
    vm.loadRouteProfileData(data);
    vm.getAllIntervalData();
  };
  content.setDateFormatRoute = (vm) => {
    vm.dateFormat = content.setDateFormat(vm.settings);
  };
  content.getAllIntervalDataRoute = (vm) => {
    if (vm.sliderMainObj && vm.sliderMainObj.length && vm.$refs.etdSliderApi) {
      content.getAllIntervalData(vm.sliderMainObj, vm, vm.allDataForInterval, vm
        .sliderMainObjSecondary);
      vm.$refs.etdSliderApi.refresh(
        vm.settings,
        vm.allDataForInterval,
        vm.sliderMainObj,
        vm.dateFormat,
      );
      if (vm.$refs.flightSliderApi) {
        vm.allDataForInterval.flightSliderInfo = {
          totalDistanceToTravel: vm.routeProfileData.totalDistanceToTravel,
          totalFlightDuration: vm.routeProfileData.totalFlightDuration,
        };
        vm.$refs.flightSliderApi.refresh(vm.settings, vm.allDataForInterval, vm.dateFormat);
        if (vm.$refs.radarSliderApi) {
          vm.$refs.radarSliderApi.refresh(vm.settings, vm.allDataForInterval, vm.dateFormat);
        }
      }
    }
  };
  content.initializeView = (vm) => {
    vm.loadRouteprofileSetting();
    vm.loadViewVairables();
    vm.initRouteProfile();
    vm.syncRouteData();
  };
  content.hideFloatingSlider = (vm) => {
    timer = setTimeout(() => {
      vm.floatingEtdStyle = {
        visibility: 'hidden',
        top: 0,
      };
    }, AppConst.timeoutFloatingEtd);
  };
  content.shiftEtdSliderLabel = (vm, sliderEventData) => {
    window.clearTimeout(timer);
    let shift;
    const minShiftFromBoundary = 2;
    let selectorId = 'floating-etd-button';
    if (vm.hideBottomAxis) {
      vm.floatingEtdStyle = {
        bottom: `${sliderEventData.cx}px`,
        right: `${utilityService.getSafeAreaPadding().right + AppConst.sliderHeightSecondary + AppConst.secondarySliderFloatPad}px`,
        visibility: 'visible',
      };
    } else {
      shift = sliderEventData.cx;
      if (sliderEventData.imageryViewUseSubnav === true
        || sliderEventData.imageryViewUseSubnav === false) {
        selectorId += sliderEventData.imageryViewUseSubnav
          ? '-primary'
          : '-secondary';
      }
      if (document.getElementById(selectorId)) {
        const widthOfEtdFloatButton = document.getElementById(selectorId).clientWidth;
        const extraSift = (widthOfEtdFloatButton / 2) - sliderEventData.leftMargin;
        shift -= extraSift;
        if (shift < minShiftFromBoundary) {
          shift = minShiftFromBoundary;
        }
        if (widthOfEtdFloatButton + sliderEventData.cx > (sliderEventData.sliderWidth
            - minShiftFromBoundary)) {
          shift = (sliderEventData.sliderWidth - widthOfEtdFloatButton) - minShiftFromBoundary;
        }
        vm.floatingEtdStyle = {
          bottom: `${utilityService.getSafeAreaPadding().bottom + AppConst.sliderHeightSecondary + AppConst.secondarySliderFloatPad}px`,
          left: `${shift}px`,
          visibility: 'visible',
        };
      }
    }
  };
  content.openQuickChangesPopover = (vm, event) => {
    popoverController.create({
      mode: 'ios',
      component: QuickChanges,
      componentProps: {
        settings: vm.settings,
      },
      event,
    }).then((popoverElement) => {
      popoverElement.present();
    });
  };
  let locked = false;
  content.openRouteSelectModal = (vm) => {
    if (locked) {
      return;
    }
    locked = true;
    modalController.create({
      component: RouteForm,
      backdropDismiss: false,
      componentProps: {
        settings: vm.settings,
      },
    }).then((modalElement) => {
      modalElement.present();
      modalElement.onDidDismiss().then((res) => {
        locked = false;
        const formData = res.data;
        if (formData) {
          vm.settings.start = formData.start;
          vm.settings.mid1 = formData.mid1;
          vm.settings.mid2 = formData.mid2;
          vm.settings.end = formData.end;
          // incase these values are missing set it to these
          if (!formData.maximumAltitudeAllowed) {
            formData.maximumAltitudeAllowed = AltitudeOptionsConst.FL20K.feet;
          }
          if (formData.showHeadWindOnly !== true && formData.showHeadWindOnly
            !== false) {
            formData.showHeadWindOnly = true;
          }
          vm.settings.maximumAltitudeAllowed = formData.maximumAltitudeAllowed;
          vm.settings.showHeadWindOnly = formData.showHeadWindOnly;
          vm.syncRouteData();
          routeProfileDataSource.routeFormSettings = utilityService.clone(vm.settings);
          etdService.departureTime = null;
          vm.initRouteProfile();
        }
      });
    });
  };
  return content;
};
export default routeControllerService();
